import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Eye, EyeOff, LogIn } from "lucide-react";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState({
    logo_url: "",
    login_bg_url: "https://images.unsplash.com/photo-1729742768363-afa1a7074d89?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBmdXJuaXR1cmUlMjBzdG9yZSUyMGludGVyaW9yJTIwYnJpZ2h0fGVufDB8fHx8MTc3MTcxMjk4MXww&ixlib=rb-4.1.0&q=85",
    company_name: "TopCocoon"
  });
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await axios.get(`${API}/branding`);
        setBranding(response.data);
      } catch (error) {
        console.log("Using default branding");
      }
    };
    fetchBranding();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userData = await login(email, password);
      toast.success("Connexion réussie !");
      const perms = userData?.permissions || [];
      // If user has dashboard access → /, otherwise go to their first accessible page
      if (perms.includes("*") || perms.includes("dashboard")) {
        navigate("/");
      } else if (perms.includes("livreur") && perms.length === 1) {
        navigate("/livreur");
      } else {
        // Find first accessible page in the catalog order
        const catalogOrder = ["ventes","stock","produits","pertes","livraisons","tournees","zones","charges","clients","devis","impayes","veille","taches","social","marketing","communications","parrainages","corbeille","parametres","livreur"];
        const pathMap = { ventes:"/ventes", stock:"/stock", produits:"/produits", pertes:"/pertes", livraisons:"/livraisons", tournees:"/tournees", zones:"/zones", charges:"/charges", clients:"/clients", devis:"/devis", impayes:"/impayes", veille:"/veille", taches:"/taches", social:"/social", marketing:"/marketing", communications:"/communications", parrainages:"/parrainages", corbeille:"/corbeille", parametres:"/parametres", livreur:"/livreur" };
        const first = catalogOrder.find(k => perms.includes(k));
        navigate(first ? pathMap[first] : "/");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Une erreur est survenue";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        backgroundImage: `url('${branding.login_bg_url}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
      data-testid="login-page"
    >
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" />
      
      <Card className="w-full max-w-md relative z-10 shadow-2xl border-0" data-testid="login-card">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-20 h-20 bg-white rounded-2xl flex items-center justify-center mb-4 shadow-lg border border-slate-200 p-2">
            <img
              src={branding.logo_url || "/logo-topcocoon.png"}
              alt={branding.company_name || "TopCocoon"}
              className="w-full h-full object-contain"
              data-testid="login-logo"
              onError={(e) => { e.currentTarget.src = "/logo-topcocoon.png"; }}
            />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-800">{branding.company_name || "TopCocoon"}</CardTitle>
          <CardDescription className="text-slate-600">
            Connectez-vous pour gérer votre activité
          </CardDescription>
        </CardHeader>
        
        <CardContent className="pt-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@topcocoon.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11"
                data-testid="email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Mot de passe</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11 pr-10"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-medium shadow-lg shadow-indigo-600/25"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              ) : (
                <>
                  <LogIn className="w-5 h-5 mr-2" />
                  Se connecter
                </>
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-xs text-slate-400">
            Accès réservé. Contactez votre administrateur pour obtenir un compte.
          </p>
          <div className="mt-3 pt-3 border-t border-slate-100 text-center">
            <a
              href="/b2b-login"
              className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
              data-testid="link-b2b-login"
            >
              🏢 Accès Portail B2B →
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
